import CoveragePurchaseLayout from "@layouts/CoveragePurchaseLayout";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { GetStaticPropsContext } from "next";
import { CmsLayout } from "@modules/cms/types/CmsLayout";
import CmsLayouts from "@modules/cms/components/CmsLayouts";
import { dehydrate } from "@tanstack/react-query";
import { getServerSideQueryClient } from "@modules/api/queryClients";
import { tryGetActiveAftermarketPurchase } from "@modules/purchase/warranty/types/Purchase";
import useAftermarketPurchase from "@modules/purchase/warranty/hooks/useAftermarketPurchase";
import getCmsPageData from "@modules/cms/api/getCmsPageData";
import SeoInfo from "@common/components/SeoInfo";
import SeoInfoProps from "@common/types/SeoInfoProps";
import { getCanonicalUrl } from "@talladega/helpers";
import Footer from "@layouts/public/Footer";
import {
  getFeatureFlagList,
  prefetchFeatureFlagList,
} from "@modules/feature-flags/hooks/useFeatureFlags";
import { FeatureFlags } from "@modules/feature-flags/types/FeatureFlag";
import RoadwiseFooter from "@layouts/public/RoadwiseFooter";

type Props = {
  layouts: CmsLayout[];
  cmsSeoInfo: SeoInfoProps | null;
  consumerFinancingEnabled: boolean;
};

const WarrantyIndexPage = ({
  layouts,
  cmsSeoInfo,
  consumerFinancingEnabled,
}: Props) => {
  const { purchase: purchaseResponse } = useAftermarketPurchase();
  const purchase = tryGetActiveAftermarketPurchase(purchaseResponse);

  return (
    <>
      <CoveragePurchaseLayout
        purchase={purchase}
        removePadding
        isOnIndex
        isOnLanding
        autoRedirectToPurchaseAfterLogin={false}
        consumerFinancingEnabled={consumerFinancingEnabled}
        showResumePurchase
      >
        {cmsSeoInfo && <SeoInfo seoInfo={cmsSeoInfo} />}
        <CmsLayouts layouts={layouts ?? []} />
      </CoveragePurchaseLayout>
      {consumerFinancingEnabled ? <RoadwiseFooter /> : <Footer />}
    </>
  );
};

WarrantyIndexPage.pageName = "WarrantyIndexPage";

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const queryClient = getServerSideQueryClient();
  await prefetchFeatureFlagList(queryClient, [
    FeatureFlags.AftermarketQuoteMethod,
  ]);

  const { layouts, seoInfo } = await getCmsPageData(
    "warranty-home",
    context.preview,
    context.locale?.toUpperCase()
  );

  if (seoInfo && !seoInfo.canonical) {
    seoInfo.canonical = getCanonicalUrl("warranty-home");
  }
  if (seoInfo && seoInfo.opengraphUrl) {
    seoInfo.opengraphUrl = getCanonicalUrl("warranty-home");
  }

  const featureFlagList = await getFeatureFlagList();

  return {
    props: {
      layouts,
      cmsSeoInfo: seoInfo,
      ...(await serverSideTranslations(context.locale ?? "en", [
        "common",
        "common-components",
        "purchase",
      ])),
      consumerFinancingEnabled: featureFlagList.some(
        (f) => f === FeatureFlags.C2CFinancing
      ),
      dehydratedState: dehydrate(queryClient),
    },
  };
};

export default WarrantyIndexPage;
